/**
 * Overlay Renderer Component
 * 
 * Dynamically renders the active overlay based on the current state.
 * Uses the modular overlay components from /components/overlays/
 */

import { Suspense, lazy } from "react";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";

// Import all overlay components
import {
  PhoneLogOverlay,
  ScheduleVisitOverlay,
  PainManagementOverlay,
  VitalSignsOverlay,
  WoundCareOverlay,
  IncidentReportOverlay,
  FamilyMessageOverlay,
  DMESuppliesOverlay,
  SymptomAssessmentOverlay,
  ConsentFormsOverlay,
  MedicationOverlay,
  CareCalendarOverlay,
  SymptomGuideOverlay,
  RecertificationOverlay,
  PhysicianOrdersOverlay,
  PlanOfCareOverlay,
  VisitFrequencyOverlay,
  PrintPatientSummaryOverlay,
  IDGDiscussionOverlay,
} from "@/components/overlays";

// Overlay component mapping
const OVERLAY_MAP = {
  phone_log: PhoneLogOverlay,
  phone_call: PhoneLogOverlay,
  schedule_visit: ScheduleVisitOverlay,
  pain_management: PainManagementOverlay,
  vital_signs: VitalSignsOverlay,
  vitals: VitalSignsOverlay,
  wound_care: WoundCareOverlay,
  incident_report: IncidentReportOverlay,
  incident: IncidentReportOverlay,
  family_message: FamilyMessageOverlay,
  family_communication: FamilyMessageOverlay,
  dme_supplies: DMESuppliesOverlay,
  dme: DMESuppliesOverlay,
  supplies: DMESuppliesOverlay,
  symptom_assessment: SymptomAssessmentOverlay,
  symptoms: SymptomAssessmentOverlay,
  consent_forms: ConsentFormsOverlay,
  consents: ConsentFormsOverlay,
  medication: MedicationOverlay,
  mar: MedicationOverlay,
  medication_administration: MedicationOverlay,
  care_calendar: CareCalendarOverlay,
  calendar: CareCalendarOverlay,
  symptom_guide: SymptomGuideOverlay,
  recertification: RecertificationOverlay,
  cti: RecertificationOverlay,
  physician_orders: PhysicianOrdersOverlay,
  orders: PhysicianOrdersOverlay,
  plan_of_care: PlanOfCareOverlay,
  poc: PlanOfCareOverlay,
  visit_frequency: VisitFrequencyOverlay,
  frequency: VisitFrequencyOverlay,
  print_summary: PrintPatientSummaryOverlay,
  print_patient_summary: PrintPatientSummaryOverlay,
  idg_discussion: IDGDiscussionOverlay,
  idg_note: IDGDiscussionOverlay,
  idg_meeting: IDGDiscussionOverlay,
};

// Overlays handled by Sheet components in HospiceOSPageContent
// These should not show error when not found in OVERLAY_MAP
const SHEET_HANDLED_OVERLAYS = [
  "visit_note", "new_visit_note", "smart_documentation",
  "idg_review", "idg_dictation", "clinical_logic",
  "hope_assessment", "ai_chat", "visit_template_builder",
  "compliance_dashboard", "voice_settings", "hope_templates",
  "iqies_export", "iqies_scheduling", "synthesis_scheduler",
  "bereavement", "care_plan", "patient_journey", "clinical_ai_agent",
  "export_report", "export", "event_detail",
  // New overlays handled by Sheet components
  "lcd_eligibility", "ai_care_plan", "rcm_management",
  "staff_scheduling", "acuity_trends", "visit_sync_admin",
  "unified_visit_note", "unified_visit",
  // Fall event and clinical assessments
  "fall_event", "fall_documentation", "fall_risk_dashboard",
  "clinical_assessments",
  // IDG voice meeting and analytics
  "idg_voice_meeting", "idg_meeting_analytics", "idg_insights_email",
  // Voice help
  "voice_help"
];

// Loading fallback
function OverlayLoading() {
  return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500" />
    </div>
  );
}

export function OverlayRenderer({ 
  activeOverlay, 
  overlayData,
  patientId, 
  patientName,
  userRole,
  onClose,
  onSave
}) {
  if (!activeOverlay) return null;

  // Normalize overlay ID
  const normalizedId = activeOverlay.toLowerCase().replace(/-/g, "_");
  
  // Check if this overlay is handled by Sheet components elsewhere
  if (SHEET_HANDLED_OVERLAYS.includes(normalizedId)) {
    return null; // Let Sheet components handle these
  }
  
  const OverlayComponent = OVERLAY_MAP[normalizedId];

  if (!OverlayComponent) {
    console.warn(`Unknown overlay: ${activeOverlay}`);
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
        <div className="bg-slate-900 border border-slate-700 rounded-xl p-6 max-w-md" onClick={e => e.stopPropagation()}>
          <h3 className="text-white font-semibold mb-2">Overlay Not Found</h3>
          <p className="text-slate-400 text-sm mb-4">
            The overlay &quot;{activeOverlay}&quot; is not available.
          </p>
          <Button onClick={onClose} className="bg-teal-600 hover:bg-teal-700">Close</Button>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={onClose}
      data-testid="overlay-backdrop"
    >
      <div 
        className="bg-slate-900 border border-slate-700 rounded-xl shadow-2xl max-w-2xl w-full mx-4 max-h-[85vh] overflow-hidden animate-in fade-in zoom-in-95 duration-200"
        onClick={e => e.stopPropagation()}
        data-testid={`overlay-${normalizedId}`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700 bg-slate-800/50">
          <span className="text-sm text-slate-400">Clinical Overlay</span>
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={onClose}
            className="text-slate-400 hover:text-white hover:bg-slate-700"
            data-testid="overlay-close-btn"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* Content */}
        <ScrollArea className="max-h-[calc(85vh-60px)]">
          <Suspense fallback={<OverlayLoading />}>
            <OverlayComponent
              patientId={patientId}
              patientName={patientName}
              userRole={userRole}
              onClose={onClose}
              onSave={onSave}
              {...overlayData}
            />
          </Suspense>
        </ScrollArea>
      </div>
    </div>
  );
}

export default OverlayRenderer;
